import {
  HHButton,
  HHInfoGrid,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import Block from '@mui/icons-material/Block';
import dayjs from 'dayjs';
import { useBillingSetting } from '../../context/billing.context';
import { useDynamicDialogContext } from '../../context/dynamic-dialog.context';
import { useDynamicDrawerContext } from '../../context/dynamic-drawer.context';
import { useBillingWriteAccess } from '../../hooks/use-billing-write-access';
import {
  SubscriptionType,
  useGetContractsV2Query,
  useGetContractTemplatesQuery,
} from '../../types';
import { displayDateTime } from '../../utils/date-helper';
import { ProgramTypeDisplay } from '../../utils/discount-utils';
import ContractDetailView from '../contract/contract-view-components/contract-detail-view';
import DetailsAccordion from '../details-accordion';
import LoadingComponent from '../utils/loading';
import SubscriptionPackageDetails from './subscription-package-details';
import { VoidSubscriptionDialog } from './void-subscription-dialog';

interface SubscriptionDetailsProps {
  subscriptionData: SubscriptionType;
}

const SubscriptionDetails = ({
  subscriptionData,
}: SubscriptionDetailsProps): JSX.Element => {
  const { value: displayLocal } = useBillingSetting('displayDateTimeLocal');
  const canWrite = useBillingWriteAccess();
  const { setDialogContent } = useDynamicDialogContext();
  const { onClose: closeDrawer } = useDynamicDrawerContext();
  const contractsQuery = useGetContractsV2Query({
    variables: {
      contractIds: [subscriptionData.contractId],
      includeVoided: true,
      page: 1,
      size: 100,
    },
  });
  const contractTemplatesQuery = useGetContractTemplatesQuery();

  if (contractsQuery.loading || contractTemplatesQuery.loading) {
    return <LoadingComponent center />;
  }

  const contract = contractsQuery.data?.getContractsV2?.items.find(
    ({ id }) => id === subscriptionData.contractId,
  );

  if (!contract || !contractTemplatesQuery.data?.getContractTemplates) {
    return (
      <HHTypography hhVariant="body">
        Contract/templates not found for this subscription
      </HHTypography>
    );
  }

  return (
    <HHStack padding={1}>
      <HHInfoGrid
        data={[
          {
            content: String(subscriptionData.id),
            heading: 'Id',
            copyable: true,
          },
          {
            content: String(contract.clientsInsurerId),
            heading: 'Client Insurer Id',
            copyable: true,
          },
          {
            content: String(contract.clientId),
            heading: 'Client Id',
            copyable: true,
          },
          {
            content: String(subscriptionData.contractId),
            heading: 'Contract Id',
            copyable: true,
          },
          {
            content: String(subscriptionData.pathwayId),
            heading: 'Pathway Id',
            copyable: true,
          },
          {
            content: String(subscriptionData.yearCount),
            heading: 'Year count',
            copyable: false,
          },
          {
            content: dayjs(subscriptionData.startsAt).format('L'),
            heading: 'Start date',
            copyable: false,
          },
          {
            content: String(subscriptionData.void),
            heading: 'Void',
            copyable: false,
          },
          {
            content:
              ProgramTypeDisplay[subscriptionData.programId ?? -1] || '--',
            heading: 'Program',
            copyable: false,
          },
          {
            content: displayDateTime(subscriptionData.createdAt, {
              displayLocal,
            }),
            heading: 'Created At',
            copyable: false,
          },
        ]}
        divided={false}
        justifyBetween={true}
      />
      {!subscriptionData.void && canWrite && (
        <HHStack spacing={2}>
          <HHTypography hhVariant="subtitle1">Actions</HHTypography>
          <HHStack direction="row">
            <HHButton
              hhVariant="variant-bypass"
              variant="outlined"
              color="error"
              startIcon={<Block />}
              data-testid="void-subscription-button"
              onClick={(): void =>
                setDialogContent({
                  title: (
                    <HHTypography hhVariant="section-title">
                      Void subscription
                    </HHTypography>
                  ),
                  content: (
                    <VoidSubscriptionDialog
                      subscriptionId={subscriptionData.id}
                      onSuccess={closeDrawer}
                    />
                  ),
                  width: 'sm',
                  fullWidth: true,
                })
              }
            >
              Void subscription
            </HHButton>
          </HHStack>
        </HHStack>
      )}
      {(subscriptionData.subscriptionPackages?.length ?? 0) > 0 && (
        <DetailsAccordion title="Subscription Packages">
          <SubscriptionPackageDetails
            subscriptionPackages={subscriptionData.subscriptionPackages ?? []}
            contractPackages={contract?.contractPackages ?? []}
          />
        </DetailsAccordion>
      )}
      <DetailsAccordion title="Contract">
        <ContractDetailView
          key={subscriptionData.contractId}
          contract={contract}
          templates={contractTemplatesQuery.data?.getContractTemplates}
          slimDisplay
        />
      </DetailsAccordion>
    </HHStack>
  );
};

export default SubscriptionDetails;
